import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, AlertCircle, Globe } from 'lucide-react';
import { useNetworkState } from '@uidotdev/usehooks';

// Custom hook to check actual internet connectivity
const useInternetConnectivity = () => {
  const [hasInternet, setHasInternet] = useState(true);
  const [isChecking, setIsChecking] = useState(false);

  const checkInternetConnectivity = async () => {
    setIsChecking(true);
    try {
      // Try to fetch a reliable external resource with a timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      await fetch('https://www.google.com/favicon.ico', {
        method: 'HEAD',
        cache: 'no-cache',
        mode: 'no-cors',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      setHasInternet(true);
    } catch (error) {
      setHasInternet(false);
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    // Check connectivity on mount
    checkInternetConnectivity();
    
    // Check every 30 seconds
    const interval = setInterval(checkInternetConnectivity, 30000);
    
    return () => clearInterval(interval);
  }, []);

  return { hasInternet, isChecking, checkInternetConnectivity };
};

const InternetStatus = ({ 
  showWhenOnline = false, 
  position = 'top-right',
  className = '',
  onStatusChange = null 
}) => {
  // Use the reliable @uidotdev/usehooks package for network state
  const { online: isNetworkOnline } = useNetworkState();
  // Use custom hook for actual internet connectivity
  const { hasInternet, isChecking } = useInternetConnectivity();
  
  // Consider online only if both network is connected AND has internet
  const isOnline = isNetworkOnline && hasInternet;
  
  const [showStatus, setShowStatus] = useState(!isOnline);
  const [wasOffline, setWasOffline] = useState(!isOnline);

  useEffect(() => {
    if (isOnline) {
      setShowStatus(true);
      setWasOffline(false);
      
      // Auto-hide after 3 seconds when coming back online
      setTimeout(() => {
        setShowStatus(false);
      }, 3000);
      
      if (onStatusChange) {
        onStatusChange(true);
      }
    } else {
      setShowStatus(true);
      setWasOffline(true);
      
      if (onStatusChange) {
        onStatusChange(false);
      }
    }
  }, [isOnline, onStatusChange]);

  // Show if offline, or if online and showWhenOnline is true, or if we just came back online
  if (!showStatus) {
    return null;
  }

  const getPositionClasses = () => {
    const baseClasses = "fixed z-[10002] transition-all duration-300 ease-in-out";
    
    const positionClasses = {
      'top-right': 'top-4 right-4',
      'top-left': 'top-4 left-4',
      'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
      'bottom-right': 'bottom-4 right-4',
      'bottom-left': 'bottom-4 left-4',
      'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2'
    };

    return `${baseClasses} ${positionClasses[position]}`;
  };

  const getStatusClasses = () => {
    if (isOnline) {
      return "bg-green-50 border-green-200 text-green-800";
    } else if (isNetworkOnline && !hasInternet) {
      return "bg-yellow-50 border-yellow-200 text-yellow-800";
    } else {
      return "bg-red-50 border-red-200 text-red-800";
    }
  };

  const getIcon = () => {
    if (isOnline) {
      return <Globe className="w-4 h-4" />;
    } else if (isNetworkOnline && !hasInternet) {
      return <Wifi className="w-4 h-4" />;
    } else {
      return <WifiOff className="w-4 h-4" />;
    }
  };

  const getMessage = () => {
    if (isOnline) {
      return wasOffline ? "Internet connection restored" : "You're online";
    } else if (isNetworkOnline && !hasInternet) {
      return isChecking ? "Checking internet..." : "Connected but no internet";
    } else {
      return "No network connection";
    }
  };

  if (!showStatus) {
    return null;
  }

  return (
    <div className={`${getPositionClasses()} ${className}`}>
      <div className={`flex items-center px-4 py-3 rounded-lg shadow-sm border ${getStatusClasses()} ${isOnline ? 'animate-pulse' : ''}`}>
        <div className="flex items-center">
          {getIcon()}
          <span className="ml-2 text-sm font-medium">
            {getMessage()}
          </span>
        </div>
        
        {!isOnline && (
          <div className="ml-3">
            <AlertCircle className="w-4 h-4 text-red-600" />
          </div>
        )}
      </div>
    </div>
  );
};

// Hook for using internet status in components
export const useInternetStatus = () => {
  const { online: isNetworkOnline } = useNetworkState();
  const { hasInternet, isChecking, checkInternetConnectivity } = useInternetConnectivity();
  
  // Consider online only if both network is connected AND has internet
  const isOnline = isNetworkOnline && hasInternet;

  return {
    isOnline,
    isNetworkOnline,
    hasInternet,
    isChecking,
    checkConnection: checkInternetConnectivity
  };
};

export { InternetStatus };
export default InternetStatus;
